'use client';

// Since QueryClientProvider relies on useContext under the hood, we have to put 'use client' on top
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createContext, ReactNode, useMemo } from 'react';
import { StoreType, DexterCampaigns, DeviceType } from '@frodo/models/types';
import { PageType } from '@frodo/models/enums';
import { getQueryClient } from '@frodo/lib/tanstack/queryClient';

type GeneralProviderProps = {
  children: ReactNode;
  store: StoreType;
  campaigns: DexterCampaigns;
  device: DeviceType;
  pageType: PageType;
};

export type GeneralContextType = {
  store: StoreType;
  campaigns: DexterCampaigns;
  device: DeviceType;
  pageType: PageType;
  // user?: UserType; // in the future we can add more things here
};

export const GeneralContext = createContext<GeneralContextType | null>(null);

export default function GeneralProvider({
  children,
  store,
  campaigns,
  device,
  pageType,
}: GeneralProviderProps) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();

  const value = useMemo(
    () => ({
      store,
      campaigns,
      device,
      pageType,
    }),
    [store, campaigns, device, pageType],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <GeneralContext.Provider value={value}>
        {children}
      </GeneralContext.Provider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}
